// Reasons and whole rows repeated across a FAMILY of capabilities, named once so
// the family cannot drift into three slightly different accounts of one decision.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. Absent from the barrel: these are the manifest's own vocabulary,
// and a caller outside it reads the ROW, never the reason that built it.

import { type Capability, gap, na, yes } from './surface';

// Reasons repeated across a family of rows, named once so the family cannot
// drift into three slightly different accounts of one decision.
export const BOX_TOKEN_LEG = na(
  'a box-token leg mounted before the /v1 bearer guard — the principal is a machine, not a user',
);
export const NO_PRINCIPAL = na('infrastructure endpoint with no principal to be at parity for');
// NOT "credentials do not cross that boundary". That sentence stood here until it was
// checked against the catalog it describes, and it is false: `billing key_add` /
// `key_rotate`, `account keys_add` / `keys_rotate` and `billing log_sink_set` all take a
// CALLER-SUPPLIED credential today (a raw provider key; a `postgres://` url with the
// credentials embedded), sealed under the KEK and never echoed back. It also mis-cited
// `config.set`'s refusal as its evidence: that guard is a STORE rule — `prompt_vars` is
// plaintext jsonb, interpolated into every prompt and returned verbatim by `config get` —
// and `PUT /v1/prompt-vars` calls the SAME `promptVarSecretRefusal` at its own edge, so it
// refuses on BOTH doors and says nothing about this one. There is no blanket rule to
// inherit here; each refusal is earned at its own row (`integration.connect` has no verb,
// `sso set` carries no `client_secret`), and the two vaults earn theirs like this:
export const SECRETS_STAY_REST = na(
  'the agent is who these values are DELIVERED to, never a caller of them: the repo vault ' +
    'is sealed onto each claim (`listSealableForAssignment`) and the account vault is ' +
    'injected server-side at session start (`buildMcpTokenResolver`), so a verb adds nothing ' +
    'a box needs and adds account-wide enumeration plus rewrite of the credential set every ' +
    'later box materializes. The NAMES are the disclosure — learning a repo carries a ' +
    '`STRIPE_LIVE_KEY` in `prod` is itself the leak — which is why both faces are owner-only ' +
    'including the READ (#1418, `OWNER_ONLY_SCOPES`). The one secret write MCP carries is ' +
    '`blocker submit` (epic #3756), on the same `write:secrets` scope as its REST twin: it ' +
    'fills only the names an open access form asks for, each once, and lists nothing',
);
export const PUSH_IS_BROWSER = na(
  'Web Push targets a browser service worker registration minted by the browser itself, so ' +
    'there is no agent subscriber and nothing an MCP caller could register — unlike the inbox ' +
    'and the preference matrix beside it, which the `notification` verb does serve',
);
export const OPERATOR_MCP_ONLY = na(
  'operator-token-only on the MCP `user`/`account` verbs — the grant writes are the ONE ' +
    'mutation on /v1/admin/* (docs/idea/api.md)',
);
export const OAUTH_DISCOVERY: Capability = {
  rest: yes,
  mcp: na(
    'these documents are how a hosted MCP client learns to authenticate BEFORE it holds a ' +
      'credential — a JSON-RPC verb serving them would need the very credential they exist ' +
      'to help mint',
  ),
  ui: na(
    'machine discovery documents an OAuth client fetches; the human path to the same ' +
      'authority is the PAT screen at `/org/:org/account/tokens`',
  ),
};
/**
 * The three legs of the OAuth grant. They share both absent-surface verdicts: the
 * flow exists to MINT the credential an MCP call already needs (so a verb serving
 * it would need what it produces), and its human half is the consent page these
 * routes render themselves — served mid-flow to a browser that may never have
 * loaded the dashboard, which is exactly why it cannot be a dashboard screen.
 */
export const OAUTH_GRANT: Capability = {
  rest: yes,
  mcp: na(
    'the leg that mints the credential an MCP client authenticates WITH — a verb serving it ' +
      'would need the very token it issues, and the consent step is a browser navigation no ' +
      'JSON-RPC call can perform',
  ),
  ui: na(
    'the human surface IS the consent page this route renders, asset-free and mid-flow; a ' +
      'dashboard screen would be a second place the same grant is approved. The dashboard owns ' +
      'the AFTERMATH instead — the issued token lists and revokes at `/org/:org/account/tokens`',
  ),
};
/**
 * Dynamic client registration, which does NOT share the grant legs' verdicts
 * above: it mints no credential (a `client_id` authorizes nothing on its own),
 * and it has no human half at all — the connector's own software performs it.
 */
export const OAUTH_REGISTRATION: Capability = {
  rest: yes,
  mcp: na(
    'a client registers BEFORE it can reach the MCP endpoint at all, so a verb serving it would ' +
      'require the very connection the registration exists to make possible',
  ),
  ui: na(
    "no human performs this step: the connector's own software registers itself, which is why " +
      'RFC 7591 exists at all. The human way to name a credential by hand is the PAT screen at ' +
      '`/org/:org/account/tokens`',
  ),
};
/**
 * The one artifact capability the browser deliberately does NOT reach. A put is
 * TWO legs — the API mints the row plus a presigned PUT, then the CALLER sends the
 * bytes straight to the bucket — and leg two from a browser is a cross-origin PUT
 * that needs a CORS policy on the bucket. For a BYO bucket that policy is the
 * customer's to write and the control plane can neither set it nor probe it, so an
 * upload button would land leg one (an audited row) and lose leg two for an
 * unknowable subset of accounts: a bytes-less artifact, which then 404s on
 * download and is refused a share. Producing artifacts is also what an AGENT does;
 * the human half is reading them, which `/org/:org/artifacts` serves.
 */
export const NO_ARTIFACT_UPLOAD_UI = na(
  'a put is two legs and a browser cannot finish the second: the bytes go cross-origin ' +
    'straight to the bucket, which needs a CORS policy the control plane neither sets (a BYO ' +
    "bucket is the customer's) nor can probe. An upload button would therefore mint audited " +
    'rows whose bytes never land for an unknowable subset of accounts, and a bytes-less ' +
    "artifact 404s on download and is refused a share. Producing artifacts is the agent's " +
    'half; the human half is reading them, at `/org/:org/artifacts`',
);
export const NO_KB_PAGE_AUTHORING_UI = gap(
  'the repo workspace KB tab (`components/RepoKbTab.tsx`) browses, searches and reads a page; ' +
    'it does not author one. Deliberately still a `gap` and not an `na`: nothing about a page ' +
    'write is agent-shaped, so a maintainer correcting a wrong page in the console is a ' +
    "first-class caller — the editor simply is not built yet. It is the KB's last open surface",
);
export const NO_RELEASE_LEDGER_REPAIR_UI = gap(
  'un-yanking a halted release is an OPERATOR act on the platform-global `runner_releases` ' +
    'ledger (`admin:releases`, which no tenant session carries), not a tenant one — every ' +
    'sibling on this plane is the caller fleet. The console reads the ledger already: the ' +
    "rollout dialog's build picker is `box.releases`, narrowed to installable rows. The repair " +
    'belongs with the other operator surfaces behind `<AdminGate/>`, and is not built yet',
);
// (#2681) The per-conversation rollup is MCP-only today. The `conversation_ref`
// flows from a chat client to a scout session to tasks, and no REST surface has
// ever read it; `POST /v1/plans` answers the scout half but reads by `plan_id`,
// not by `conversation_ref`. Adding a REST twin would be a new public
// capability (a new route, a new OpenAPI row), and the read's only consumer
// today is the MCP `plan.rollup` action — surfacing it elsewhere is owed only
// when a second consumer names itself.
export const NO_PLAN_ROLLUP_REST = na(
  'no REST surface reads `tasks.conversation_ref` (#2681) — `POST /v1/plans` answers by `plan_id`; adding a REST twin is a new public capability and is owed only when a second consumer names itself',
);
